import React, { useState, useEffect } from 'react';
import './FrequencyLayouts.css';

function FrequencyCircle({ frequencies, activeFrequency, favorites = [], onActivate, onToggleFavorite, isDisabled = false }) {
  const [isMobile, setIsMobile] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 700, height: 700 });

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Calculate responsive container size
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let size;
      if (viewportWidth < 480) {
        // Small mobile
        size = Math.min(viewportWidth - 40, viewportHeight - 300, 380);
      } else if (viewportWidth < 768) {
        // Large mobile / tablet portrait
        size = Math.min(viewportWidth - 60, viewportHeight - 300, 500);
      } else if (viewportWidth < 1024) {
        // Tablet landscape
        size = Math.min(viewportWidth - 100, viewportHeight - 200, 600);
      } else {
        // Desktop
        size = 700;
      }
      
      setContainerSize({ width: size, height: size });
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate radius based on container size
  const baseRadius = containerSize.width / 2.5; // Responsive radius
  const centerX = 0;
  const centerY = 0;
  const angleStep = (2 * Math.PI) / frequencies.length;

  // Responsive sizing
  const buttonSize = isMobile ? 60 : 80;
  const iconSize = isMobile ? '1.5rem' : '2rem';
  const labelFontSize = isMobile ? '0.75rem' : '0.875rem';
  const centerLogoSize = isMobile ? 60 : 100;

  return (
    <div className="frequency-circle-container" data-testid="frequency-circle-container">
      <div 
        className="frequency-circle"
        style={{
          width: `${containerSize.width}px`,
          height: `${containerSize.height}px`,
        }}
      >
        {/* Center Logo */}
        <div className="circle-center" style={{ width: `${centerLogoSize + 40}px`, height: `${centerLogoSize + 40}px` }}>
          <img
            src="https://customer-assets.emergentagent.com/job_f48ed865-f137-40ec-8ec5-750617ca0014/artifacts/jm5y5oub_logo.png"
            alt="ZENTA"
            className="center-logo"
            style={{ width: `${centerLogoSize}px` }}
          />
          {activeFrequency && (
            <div
              className="center-glow"
              style={{ 
                backgroundColor: activeFrequency.color_hex,
                width: `${centerLogoSize * 2}px`,
                height: `${centerLogoSize * 2}px`
              }}
            ></div>
          )}
        </div>

        {/* Frequency Items in Circle - No favorites icon */}
        {frequencies.map((freq, index) => {
          const angle = angleStep * index - Math.PI / 2;
          const x = centerX + baseRadius * Math.cos(angle);
          const y = centerY + baseRadius * Math.sin(angle);
          const isActive = activeFrequency?.id === freq.id;

          return (
            <div
              key={freq.id}
              className={`circle-item ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
              style={{
                '--freq-color': freq.color_hex,
                left: '50%',
                top: '50%',
                transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
              }}
              data-testid={`frequency-circle-${freq.id}`}
            >
              <button
                className="circle-button"
                onClick={() => onActivate(freq.id)}
                style={{
                  width: `${buttonSize}px`,
                  height: `${buttonSize}px`,
                  backgroundColor: isActive
                    ? freq.color_hex
                    : 'rgba(23, 37, 52, 0.9)',
                  borderColor: freq.color_hex,
                  color: isActive ? '#000' : freq.color_hex,
                  boxShadow: isActive
                    ? `0 0 ${isMobile ? '20px' : '30px'} ${freq.color_hex}`
                    : 'none',
                }}
                data-testid={`activate-button-${freq.id}`}
              >
                <span className="circle-icon" style={{ fontSize: iconSize }}>{freq.icon}</span>
              </button>
              <div 
                className="circle-label" 
                style={{ 
                  fontSize: labelFontSize,
                  maxWidth: `${buttonSize + 20}px`
                }}
              >
                {freq.name}
              </div>
              {isActive && (
                <div 
                  className="circle-active-indicator"
                  style={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                >
                  ✓
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FrequencyCircle;
